import {useEffect, useState} from 'react'
import styled from 'styled-components'
import {Autocomplete, Button, CircularProgress, TextField} from '@mui/material'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import {toast} from 'react-toastify'
import {getCurrentColors} from '../../utils/colors'
import LeftBar from '../home/components/leftBar'
import {FlowApi} from '../../services/FlowApi.service'

const COLORS = getCurrentColors()

type FlowOption = {id: string; name: string}
type OrganizationOption = {id: string; name: string; document_flow_id: string | null}
type LinkedOrganization = {id: string; name: string; document_flow_active: boolean}

const Page = styled.div`
    display: flex;
    height: 100vh;
    overflow: hidden !important;
`

const Content = styled.div`
    flex: 1;
    padding: 64px 48px;
    background-color: #f6f8fa;
    overflow-y: auto;
`

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
    max-width: 600px;
`

const LinkedList = styled.ul`
    list-style: none;
    padding: 0;
    margin-top: 24px;
    max-width: 600px;
`

const LinkedItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${COLORS.branco};
    box-shadow: 1px 1px 3px 1px #d4d3d3;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 10px;
`

const flowApi = new FlowApi()

export default function FlowOrganizationsPage() {
    const [flows, setFlows] = useState<FlowOption[]>([])
    const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
    const [selectedFlow, setSelectedFlow] = useState<FlowOption | null>(null)
    const [selectedOrg, setSelectedOrg] = useState<OrganizationOption | null>(null)
    const [linkedOrgs, setLinkedOrgs] = useState<LinkedOrganization[]>([])
    const [loadingLinked, setLoadingLinked] = useState(false)
    const [linking, setLinking] = useState(false)

    useEffect(() => {
        const loadData = async () => {
            try {
                const [flowsData, orgsData] = await Promise.all([
                    flowApi.getFlow(),
                    flowApi.getAllOrganizations(),
                ])
                setFlows(Array.isArray(flowsData) ? flowsData : [])
                setOrganizations(Array.isArray(orgsData) ? orgsData : [])
            } catch (error) {
                toast.error('Erro ao carregar fluxos e organizações')
            }
        }
        loadData()
    }, [])

    useEffect(() => {
        setSelectedOrg(null)
        if (!selectedFlow) {
            setLinkedOrgs([])
            return
        }
        const loadLinked = async () => {
            setLoadingLinked(true)
            try {
                const data = await flowApi.getFlowOrganizations(selectedFlow.id)
                setLinkedOrgs(Array.isArray(data) ? data : [])
            } catch (error) {
                toast.error('Erro ao carregar organizações vinculadas')
            } finally {
                setLoadingLinked(false)
            }
        }
        loadLinked()
    }, [selectedFlow])

    const refreshLinked = async () => {
        if (!selectedFlow) return
        try {
            const data = await flowApi.getFlowOrganizations(selectedFlow.id)
            setLinkedOrgs(Array.isArray(data) ? data : [])
        } catch (error) {
            toast.error('Erro ao atualizar organizações vinculadas')
        }
    }

    const handleLink = async () => {
        if (!selectedFlow || !selectedOrg) return
        if (
            selectedOrg.document_flow_id &&
            selectedOrg.document_flow_id !== selectedFlow.id &&
            !window.confirm(
                `${selectedOrg.name} já está vinculada a outro fluxo. Deseja substituir o vínculo?`
            )
        ) {
            return
        }
        setLinking(true)
        try {
            await flowApi.updateOrganizationFlow(selectedOrg.id, {
                document_flow_id: selectedFlow.id,
                document_flow_active: true,
            })
            toast.success('Fluxo vinculado com sucesso')
            setSelectedOrg(null)
            await refreshLinked()
        } catch (error) {
            toast.error('Erro ao vincular fluxo à organização')
        } finally {
            setLinking(false)
        }
    }

    const handleUnlink = async (organization_id: string, organization_name: string) => {
        if (!window.confirm(`Desvincular o fluxo de "${organization_name}"?`)) return
        try {
            await flowApi.updateOrganizationFlow(organization_id, {
                document_flow_id: null,
                document_flow_active: false,
            })
            toast.success('Fluxo desvinculado com sucesso')
            await refreshLinked()
        } catch (error) {
            toast.error('Erro ao desvincular fluxo da organização')
        }
    }

    return (
        <Page>
            <LeftBar />
            <Content>
                <h1>Fluxos x Organizações</h1>
                <p style={{color: '#707070'}}>
                    Vincule ou desvincule fluxos de documentos às organizações
                </p>

                <Row>
                    <Autocomplete
                        fullWidth
                        options={flows}
                        getOptionLabel={(flow) => flow.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedFlow}
                        onChange={(_, value) => setSelectedFlow(value)}
                        renderInput={(params) => (
                            <TextField {...params} label="Fluxo de documentos" />
                        )}
                    />
                </Row>

                {selectedFlow && (
                    <>
                        <Row>
                            <Autocomplete
                                fullWidth
                                options={organizations}
                                getOptionLabel={(org) => org.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedOrg}
                                onChange={(_, value) => setSelectedOrg(value)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Organização" />
                                )}
                            />
                            <Button
                                variant="contained"
                                disabled={!selectedOrg || linking}
                                onClick={handleLink}
                            >
                                {linking ? <CircularProgress size={20} /> : 'Vincular'}
                            </Button>
                        </Row>

                        <h3 style={{marginTop: 32}}>Organizações vinculadas</h3>
                        {loadingLinked ? (
                            <CircularProgress size={24} />
                        ) : linkedOrgs.length === 0 ? (
                            <p style={{color: '#707070'}}>Nenhuma organização vinculada a este fluxo</p>
                        ) : (
                            <LinkedList>
                                {linkedOrgs.map((org) => (
                                    <LinkedItem key={org.id}>
                                        <span>
                                            {org.name}{' '}
                                            <small style={{color: org.document_flow_active ? '#39cab7' : '#c4c3c3'}}>
                                                ({org.document_flow_active ? 'ativo' : 'inativo'})
                                            </small>
                                        </span>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<LinkOffIcon />}
                                            onClick={() => handleUnlink(org.id, org.name)}
                                        >
                                            Desvincular
                                        </Button>
                                    </LinkedItem>
                                ))}
                            </LinkedList>
                        )}
                    </>
                )}
            </Content>
        </Page>
    )
}
